import type { AxiosResponse } from 'axios'

import { AdminApiError, adminClient } from '@/api/clients/admin-client'
import { getAdminProfile } from '@/api/admin/auth'
import type { ApiResponse } from '@/shared/types/http'
import type {
  AdminApplicationData,
  AdminDictionaryItem,
  AdminMenuRouteSource
} from '@/shared/types/admin'

const requireArrayData = <T>(response: AxiosResponse<ApiResponse<T[]>>): T[] => {
  if (!Array.isArray(response.data.data)) {
    throw new AdminApiError('Admin 响应数据格式错误', response.data.code)
  }

  return response.data.data
}

const getAdminMenus = async (): Promise<AdminMenuRouteSource[]> => {
  const response = await adminClient.get<ApiResponse<AdminMenuRouteSource[]>>('/sys/menu/nav')
  return filterCanceledAdminMenus(requireArrayData(response))
}

const CANCELED_ADMIN_MENU_URLS = new Set([
  'message/mail_log',
  'message/sms',
  'message/sms-log'
])

const normalizeMenuUrlForPolicy = (url: string | null | undefined): string => {
  return (url || '')
    .trim()
    .split(/[?#]/, 1)[0]
    ?.replaceAll('\\', '/')
    .replace(/^\/+/, '')
    .replace(/^admin\//, '') || ''
}

/**
 * ADR-016/017 明确取消的菜单不进入统一 Admin，避免产生可点击的空路由。
 */
export const filterCanceledAdminMenus = (
  menus: readonly AdminMenuRouteSource[]
): AdminMenuRouteSource[] => {
  return menus.flatMap(menu => {
    if (CANCELED_ADMIN_MENU_URLS.has(normalizeMenuUrlForPolicy(menu.url))) return []
    return [{
      ...menu,
      children: menu.children ? filterCanceledAdminMenus(menu.children) : undefined
    }]
  })
}

const getAdminPermissions = async (): Promise<string[]> => {
  const response = await adminClient.get<ApiResponse<string[]>>('/sys/menu/permissions')
  return requireArrayData(response)
}

const getAdminDictionaries = async (): Promise<AdminDictionaryItem[]> => {
  const response = await adminClient.get<ApiResponse<AdminDictionaryItem[]>>('/sys/dict/type/all')
  return requireArrayData(response)
}

export const loadAdminApplicationData = async (): Promise<AdminApplicationData> => {
  const [user, menus, permissions, dictionaries] = await Promise.all([
    getAdminProfile(),
    getAdminMenus(),
    getAdminPermissions(),
    getAdminDictionaries()
  ])

  return { user, menus, permissions, dictionaries }
}
